import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_PLAY_BOARDS_PRO } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { cart, glossary, mobileApp } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { css } from '@sorare/css';

import { CardProperties } from '#src/components/card/CardProperties/index.tsx';
import { AddToCart } from '#src/components/cart/AddToCart/index.tsx';
import { PlayerGameStatsPrediction } from '#src/components/game/PlayerGameStatsPrediction/index.tsx';
import { PriceLockedRibbon } from '#src/components/primaryBuy/PriceLockedRibbon/index.tsx';
import { PrimaryOfferPrice } from '#src/components/primaryBuy/PrimaryBuyBuyField/Price/index.tsx';
import { PrimaryBuyBuyField } from '#src/components/primaryBuy/PrimaryBuyBuyField/index.tsx';
import { PrimaryOfferStockCounter } from '#src/components/primaryBuy/PrimaryOfferStockCounter/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';

import type { MatchdayPickCard_instantBuyCampaign } from './__generated__/index.graphql.ts';

const cornerBadges = css`
  position: absolute;
  top: var(--unit);
  right: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--half-unit);
`;

type Props = {
  campaign: MatchdayPickCard_instantBuyCampaign;
  onInfoClick?: (opened: { cardSlug: string; playerSlug: string }) => void;
};

export const MatchdayPickCard = ({ campaign, onInfoClick }: Props) => {
  const { generateSportContextPath } = useSportContext();

  const { primaryOffer, player } = campaign;
  const card = primaryOffer?.anyCards[0];
  const [nextGameStats] = player?.anyFutureGameStats ?? [];
  const startingOdds = isType(nextGameStats, 'PlayerGameStats')
    ? nextGameStats.footballPlayingStatusOdds
    : null;

  if (!primaryOffer || !card) return null;

  const { isCampaign, soldOut, color } = getSteppedPrimaryOffer(primaryOffer);

  const openPlayerDetails = onInfoClick
    ? () =>
        onInfoClick({ cardSlug: card.slug, playerSlug: card.anyPlayer.slug })
    : undefined;

  // `primaryOffer` falls back to the last settled sale whenever none is on sale, so an active
  // campaign can still have no buyable offer. PrimaryBuyBuyField renders nothing in that case.
  const buyable = !soldOut && !!card.livePrimaryOffer;

  const buyAction = !buyable ? (
    <Button color="quaternary" size="small" fullWidth disabled>
      {soldOut ? (
        <FormattedMessage {...cart.soldOut} />
      ) : (
        <FormattedMessage {...mobileApp.unavailable} />
      )}
    </Button>
  ) : (
    <PrimaryBuyBuyField
      card={card}
      size="small"
      color="quaternary"
      fullWidth
      origin={ClickInstantBuy_Flow.MARKETPLACE}
      primaryBuyConfirmationOptions={{
        actions: {
          primary: { label: <FormattedMessage {...glossary.gotIt} /> },
          secondary: {
            to: generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO, {
              sport: card.sport,
            }),
            label: <FormattedMessage {...glossary.playInPro} />,
          },
        },
      }}
    />
  );

  return (
    <Vertical gap={1}>
      <div className="relative">
        <FlexCard card={card} width={160} withLink />
        <div className={cornerBadges}>
          <PriceLockedRibbon primaryOffer={primaryOffer} position="inline" />
          {!!startingOdds && (
            <PlayerGameStatsPrediction
              playingStatusOdds={startingOdds}
              variant="cornerBadge"
            />
          )}
        </div>
      </div>

      <CardProperties
        card={card}
        cardWithStats={card}
        withTransferMalus={false}
        playerScoreSmall
        onInfoClick={openPlayerDetails}
      />

      <Horizontal gap={0.5}>
        {!!isCampaign && (
          <FontAwesomeIcon icon={iconLock} size="sm" color={color} />
        )}
        <PrimaryOfferPrice primaryOffer={primaryOffer} />
      </Horizontal>

      {buyAction}

      {!!buyable && (
        <AddToCart
          anyCard={card}
          size="small"
          color="tertiary"
          origin={ClickAddToCart_Origin.MARKETPLACE}
          fullWidth
        >
          <FormattedMessage {...cart.addToCart} />
        </AddToCart>
      )}

      <PrimaryOfferStockCounter primaryOffer={primaryOffer} size="compact" />
    </Vertical>
  );
};

MatchdayPickCard.fragments = {
  instantBuyCampaign: gql`
    fragment MatchdayPickCard_instantBuyCampaign on InstantBuyCampaign {
      id
      player {
        slug
        anyFutureGameStats(first: 1) {
          id
          ... on PlayerGameStats {
            id
            footballPlayingStatusOdds {
              ...PlayerGameStatsPrediction_playingStatusOdds
            }
          }
        }
      }
      primaryOffer {
        id
        anyCards {
          slug
          sport
          livePrimaryOffer {
            id
          }
          ...FlexCard_anyCard
          ...CardProperties_anyCard
          ...CardPropertiesWithStats_anyCard
          ...PrimaryBuyBuyField_anyCard
          ...AddToCart_anyCard
        }
        ...SteppedPrimaryOffer_primaryOffer
        ...PriceLockedRibbon_primaryOffer
        ...PrimaryOfferPrice_primaryOffer
        ...PrimaryOfferStockCounter_primaryOffer
      }
    }
    ${FlexCard.fragments.anyCard}
    ${CardProperties.fragments.anyCard}
    ${CardProperties.fragments.anyCardWithStats}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${AddToCart.fragments.anyCard}
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
    ${PriceLockedRibbon.fragments.primaryOffer}
    ${PrimaryOfferPrice.fragments.primaryOffer}
    ${PrimaryOfferStockCounter.fragments.primaryOffer}
    ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
  ` as TypedDocumentNode<MatchdayPickCard_instantBuyCampaign>,
};
